import { useCallback } from 'react';
import { TransferJobTaskStatus } from '@frameio/graphql-client/client-schema/transfers/state/transferJobTaskStatus';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useFinderRefetch } from '@frameio/shared/contexts/FinderRefetchContext';
import { createOrUpdateTransferState } from '@frameio/transfers/utils';
import { useInitializeAssetsUploadMutation } from '../gql';
import {
  filterUploadUrls,
  useUxpTransferMutations,
} from './useUxpTransferMutations';

export type DeferredPlaceholder = {
  assetId: string;
  transferBatchId: string;
  projectId: string;
};

/**
 * Creates a deferred-size placeholder asset (via `addAssetsToTransferBatch`
 * with `deferUpload: true`) so the Finder shows a card while the NLE encodes;
 * `initializeAssetsUpload` later sets the real size and mints upload URLs.
 */
export const useCreateDeferredPlaceholder = () => {
  const activeAccountId = useActiveAccountId();
  const refetchFinder = useFinderRefetch();

  const { addAssetsToTransferBatch, createTransferBatch } =
    useUxpTransferMutations();

  return useCallback(
    async function createDeferredPlaceholder({
      fileName,
      filePath,
      folderAssetId,
    }: {
      fileName: string;
      filePath: string;
      folderAssetId: string;
    }): Promise<DeferredPlaceholder> {
      const result = await createTransferBatch({
        variables: {
          input: {
            accountId: activeAccountId,
            name: fileName,
            uploadedVia: 'DESKTOP',
            topLevelFileCount: 1,
          },
        },
      });
      const transferBatchId =
        result?.data?.createTransferBatch?.transferBatch?.id;
      if (!transferBatchId) throw new Error('Failed to create transfer batch');

      const addAssetsResult = await addAssetsToTransferBatch({
        variables: {
          input: {
            transferBatchId,
            deferUpload: true,
            assets: [
              {
                parentId: folderAssetId,
                name: fileName,
                type: 'file',
              },
            ],
          },
        },
      });

      const asset =
        addAssetsResult?.data?.addAssetsToTransferBatch?.assetItems?.[0]?.asset;
      if (!asset?.id) throw new Error('Failed to create placeholder asset');

      createOrUpdateTransferState(asset.id, {
        status: TransferJobTaskStatus.UPLOADING,
        isEncoding: true,
        assetId: asset.id,
        filePath,
      });

      refetchFinder();

      return {
        assetId: asset.id,
        transferBatchId,
        projectId: asset.project?.id ?? '',
      };
    },
    [
      activeAccountId,
      addAssetsToTransferBatch,
      createTransferBatch,
      refetchFinder,
    ]
  );
};

/**
 * Finalizes a deferred placeholder once the encoded file exists: sets its real
 * size via `initializeAssetsUpload` and starts the native upload.
 */
export const useInitializeDeferredTransfer = () => {
  const [initializeAssetsUpload] = useInitializeAssetsUploadMutation();
  const { createUXPTransferJob, startTransferJob, updateTransferBatch } =
    useUxpTransferMutations();

  return useCallback(
    async function initializeDeferredTransfer({
      batchItemId,
      filePath,
      fileName,
      assetId,
      transferBatchId,
    }: {
      batchItemId: string;
      filePath: string;
      fileName: string;
      assetId: string;
      transferBatchId: string;
    }) {
      // updateTransferBatch only needs transferBatchId, so it runs in parallel.
      const [{ data: uxpTransferJobData }] = await Promise.all([
        createUXPTransferJob({
          variables: {
            batchItemId,
            filePaths: [filePath],
            originalFileNames: [fileName],
          },
        }),
        updateTransferBatch({
          variables: {
            input: {
              id: transferBatchId,
              status: 'PROCESSING',
            },
          },
        }),
      ]);

      const uxpTransferJob = uxpTransferJobData?.createUploadTransferJob;
      const filePayload = uxpTransferJob?.assets?.[0];
      if (filePayload?.filesize == null) {
        throw new Error('UXP transfer job returned no file size');
      }

      const { data: initializeResult } = await initializeAssetsUpload({
        variables: {
          input: {
            assets: [
              {
                assetId,
                filesize: filePayload.filesize,
                filetype: filePayload.fileType,
              },
            ],
          },
        },
      });

      // All-or-nothing: failures reject (handled by the caller's catch); a
      // resolved mutation always carries the initialized asset.
      const initialized =
        initializeResult?.initializeAssetsUpload?.assets?.nodes?.[0];
      if (!initialized?.id) {
        throw new Error('initializeAssetsUpload returned no asset');
      }

      createOrUpdateTransferState(assetId, {
        status: TransferJobTaskStatus.UPLOADING,
        // Encoding is done here; clearing the flag also arms the one-way guard
        // against late encode-progress events.
        isEncoding: false,
        assetId,
        jobId: uxpTransferJob?.id,
        filePath,
      });

      await startTransferJob({
        variables: {
          input: {
            jobId: batchItemId,
            assets: [
              {
                assetId,
                uploadUrls: filterUploadUrls(initialized.uploadUrls),
              },
            ],
          },
        },
      });

      return {
        jobId: uxpTransferJob?.id,
        totalFileSize: filePayload.filesize,
        projectId: initialized.project?.id ?? '',
      };
    },
    [
      createUXPTransferJob,
      initializeAssetsUpload,
      startTransferJob,
      updateTransferBatch,
    ]
  );
};
